import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'

import { cn } from '@/lib/utils'

import type { VariantProps } from 'class-variance-authority'
import type * as React from 'react'

const buttonVariants = cva(
  cn(
    `w-full max-w-md`,
    `relative cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium`,
    `transition-all transition-color duration-200 ease-in-out`,
    `disabled:pointer-events-none disabled:opacity-50`,
    `focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]`,
    `active:shadow-none active:duration-100`,
    `aria-invalid:border-destructive aria-invalid:ring-destructive/20`,
    `border border-transparent`,
    `[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-6 [&_svg]:shrink-0 [&_svg]:cursor-pointer`
  ),
  {
    variants: {
      variant: {
        default: cn(
          'bg-primary text-primary-foreground/90 shadow-xs',
          'hover:shadow-md hover:bg-accent-hover hover:border-accent hover:text-accent-foreground'
        ),
        destructive: cn(
          'bg-destructive text-destructive-foreground shadow-xs',
          'hover:shadow-md hover:bg-destructive-hover hover:text-destructive-foreground'
        ),
        outline: cn(
          'border border-primary/40 bg-background shadow-xs text-foreground',
          'hover:shadow-md hover:bg-accent hover:text-accent-foreground hover:border-primary'
        ),
        destructiveOutline: cn(
          'border border-destructive/40 bg-background shadow-xs text-destructive',
          'hover:shadow-md hover:bg-destructive/10 hover:text-destructive hover:border-destructive'
        ),
        secondary: cn(
          'bg-secondary text-secondary-foreground shadow-xs hover:shadow-md hover:bg-secondary/80',
          'focus-visible:ring-secondary/20 outline-secondary/40',
          'dark:focus-visible:ring-secondary/40 dark:bg-secondary/60 dark:outline-secondary/60'
        ),
        secondaryOutline: cn(
          'border border-secondary/40 bg-background shadow-xs text-secondary',
          'hover:shadow-md hover:bg-secondary/10 hover:text-secondary hover:border-secondary'
        ),
        ghost: cn('hover:bg-muted/20 hover:text-foreground/70'),
        link: 'text-primary underline-offset-4 hover:underline',
        clear: '',
        input: cn(
          'font-normal !rounded-md',
          'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          'focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-[3px]',
          'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive'
        ),
      },
      size: {
        clear: 'w-auto h-auto p-0 m-0 has-[>svg]:px-0 [&_svg]:size-4 text-inherit',
        default: 'h-9 px-4 py-2 has-[>svg]:px-4',
        lg: 'h-10 rounded-xl px-6 has-[>svg]:px-6',
        sm: 'h-8 rounded-xl gap-1.5 px-3 has-[>svg]:px-3 text-xs',
        xs: 'h-5 rounded-xl gap-1 px-2 has-[>svg]:px-2 text-[.75rem]',
        icon: 'size-6 rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  const variants = variant !== 'clear' ? buttonVariants({ variant, size }) : ''

  return <Comp data-slot="button" className={cn(variants, className)} {...props} />
}

export { Button, buttonVariants }
